import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardService } from '../../../services/dashboardService';
import AdminSpinner from '../components/AdminSpinner';
import theme from '../adminTheme';

const styles = {
  container: {
    maxWidth: 1100,
    margin: '0 auto'
  },
  header: {
    marginBottom: 28
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: '#1a1a1a',
    margin: 0
  },
  titleMobile: {
    fontSize: 20
  },
  subtitle: {
    fontSize: 15,
    color: '#666',
    marginTop: 6,
    marginBottom: 0
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 20,
    marginBottom: 32
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: 20,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid #eee',
    transition: 'box-shadow 0.2s ease'
  },
  cardLabel: {
    fontSize: 13,
    color: '#666',
    fontWeight: 500,
    marginBottom: 6
  },
  cardValue: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1a4d8c'
  },
  cardSub: {
    fontSize: 12,
    color: '#888',
    marginTop: 4
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#333',
    marginBottom: 16
  },
  quickLinks: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: 12
  },
  quickLinksMobile: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 10
  },
  quickLink: {
    display: 'block',
    padding: '14px 18px',
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: 10,
    color: '#333',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    width: '100%'
  },
  loading: {
    textAlign: 'center',
    padding: 48,
    color: '#666'
  },
  error: {
    padding: 16,
    background: '#ffebee',
    color: '#c62828',
    borderRadius: 10,
    marginBottom: 20
  }
};

const DashboardView = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    DashboardService.getStats()
      .then((data) => {
        if (!cancelled) {
          setStats(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Error al cargar el resumen');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const goTo = (view) => {
    navigate(`/admin?view=${view}`, { replace: true });
  };

  if (loading) {
    return (
      <div style={{ ...theme.container, maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ marginBottom: 28 }}>
          <h1 style={theme.title}>Panel de Administración</h1>
          <p style={theme.subtitle}>Resumen general del sistema</p>
        </div>
        <AdminSpinner label="Cargando estadísticas..." />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div className="dashboard-header" style={styles.header}>
        <h1 style={styles.title}>Panel de Administración</h1>
        <p style={styles.subtitle}>Resumen general del sistema</p>
      </div>

      {error && <div style={theme.error}>{error}</div>}

      {stats && (
        <div className="dashboard-cards" style={styles.cardsGrid}>
          <div style={styles.card}>
            <div style={styles.cardLabel}>Usuarios</div>
            <div style={styles.cardValue}>{stats.usuarios?.total ?? 0}</div>
            <div style={styles.cardSub}>{stats.usuarios?.activos ?? 0} activos</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardLabel}>Gimnasios</div>
            <div style={styles.cardValue}>{stats.gyms ?? 0}</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardLabel}>Clubs</div>
            <div style={styles.cardValue}>{stats.clubs ?? 0}</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardLabel}>Afiliaciones</div>
            <div style={styles.cardValue}>{stats.afiliaciones?.total ?? 0}</div>
            <div style={styles.cardSub}>{stats.afiliaciones?.activas ?? 0} activas</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardLabel}>Entrenadores</div>
            <div style={styles.cardValue}>{stats.entrenadores ?? 0}</div>
            <div style={styles.cardSub}>relaciones</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardLabel}>Eventos</div>
            <div style={styles.cardValue}>{stats.eventos?.total ?? 0}</div>
            <div style={styles.cardSub}>{stats.eventos?.proximos ?? 0} próximos</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardLabel}>Productos</div>
            <div style={styles.cardValue}>{stats.productos ?? 0}</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardLabel}>Pedidos</div>
            <div style={styles.cardValue}>{stats.pedidos ?? 0}</div>
          </div>
        </div>
      )}

      <h2 style={styles.sectionTitle}>Accesos rápidos</h2>
      <div className="dashboard-quicklinks" style={styles.quickLinks}>
        <button type="button" className="dashboard-quicklink" style={styles.quickLink} onClick={() => goTo('gyms')}>Gimnasios</button>
        <button type="button" className="dashboard-quicklink" style={styles.quickLink} onClick={() => goTo('clubs')}>Clubs</button>
        <button type="button" className="dashboard-quicklink" style={styles.quickLink} onClick={() => goTo('productos')}>Tienda / Productos</button>
        <button type="button" className="dashboard-quicklink" style={styles.quickLink} onClick={() => goTo('usuarios')}>Usuarios</button>
        <button type="button" className="dashboard-quicklink" style={styles.quickLink} onClick={() => goTo('entrenadores')}>Entrenadores</button>
        <button type="button" className="dashboard-quicklink" style={styles.quickLink} onClick={() => goTo('afiliaciones')}>Afiliaciones</button>
        <button type="button" className="dashboard-quicklink" style={styles.quickLink} onClick={() => goTo('valores-afiliacion')}>Valores de afiliación</button>
        <button type="button" className="dashboard-quicklink" style={styles.quickLink} onClick={() => goTo('eventos')}>Eventos</button>
        <button type="button" className="dashboard-quicklink" style={styles.quickLink} onClick={() => goTo('participantes')}>Lista de participantes</button>
        <button type="button" className="dashboard-quicklink" style={styles.quickLink} onClick={() => goTo('records')}>Récords</button>
      </div>
      <style>{`
        .dashboard-quicklink:hover {
          background: #f5f8fc !important;
          border-color: #1a4d8c !important;
          color: #1a4d8c !important;
        }
        @media (max-width: 600px) {
          .dashboard-header h1 { font-size: 1.35rem !important; }
          .dashboard-header p { font-size: 0.9rem !important; }
          .dashboard-cards {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 12px !important;
          }
          .dashboard-cards > div { padding: 14px !important; }
          .dashboard-cards > div > div:first-child { font-size: 11px !important; }
          .dashboard-cards > div > div:nth-child(2) { font-size: 20px !important; }
          .dashboard-cards > div > div:nth-child(3) { font-size: 11px !important; }
          .dashboard-quicklinks {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 10px !important;
          }
          .dashboard-quicklink { font-size: 12px !important; padding: 10px 12px !important; }
        }
      `}</style>
    </div>
  );
};

export default DashboardView;
